import { useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import { authClient } from '../lib/auth-client';

// TIKTOK ATTRIBUTION — the pixel itself is loaded in public/index.html (same
// Pixel ID as the landing site). This provider handles the identity half:
//
//  1. ttq.identify() with email + external_id once a session exists. TikTok's
//     events.js SHA-256 hashes plaintext PII client-side before transmission,
//     so match quality doesn't depend on cookies surviving Safari ITP.
//  2. CompleteRegistration fired once per user. Signup itself has no single
//     client-side moment we can hook (email signups detour through email
//     verification, OAuth signups land straight on /dashboard), so instead we
//     fire on the first session where the user was created within the last
//     24 hours, guarded by a per-user localStorage flag against re-firing.

const NEW_USER_WINDOW_HOURS = 24;

const registrationTrackedKey = (userId: string) =>
  `ttq_registration_tracked_${userId}`;

interface TikTokPixelIdentifierProps {
  children: React.ReactNode;
}

export const TikTokPixelIdentifier = ({
  children,
}: TikTokPixelIdentifierProps) => {
  const { data: session } = authClient.useSession();
  const lastIdentifiedUserId = useRef<string | null>(null);

  useEffect(() => {
    const user = session?.user;
    if (!user || !window.ttq) return;
    if (lastIdentifiedUserId.current === user.id) return;

    window.ttq.identify({
      email: user.email,
      external_id: user.id,
    });
    lastIdentifiedUserId.current = user.id;

    const isNewUser =
      dayjs().diff(dayjs(user.createdAt), 'hour') < NEW_USER_WINDOW_HOURS;
    if (!isNewUser) return;

    let alreadyTracked = false;
    try {
      alreadyTracked = !!window.localStorage.getItem(
        registrationTrackedKey(user.id),
      );
    } catch {
      // localStorage unavailable (private mode) — risk a duplicate rather
      // than lose the conversion.
    }
    if (alreadyTracked) return;

    window.ttq.track('CompleteRegistration');
    try {
      window.localStorage.setItem(registrationTrackedKey(user.id), '1');
    } catch {
      // ignore
    }
  }, [session]);

  return <>{children}</>;
};
